import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
import { AgentMark } from '../ui/AgentMark';
import { ProcessingTag } from '../ui/ProcessingTag';
import { SlideToConfirm } from '../ui/SlideToConfirm';
import { WhyPopover } from '../ui/WhyPopover';
import { useDb } from '../../hooks/useDb';
import { proposalLines } from '../../utils/dbSelectors';
import { getAgent } from '../../utils/agents';
import type { Proposal } from '../../types/db';
const EASE = [0.23, 1, 0.32, 1] as const;
const SLIDE: Record<Proposal['kind'], {
  label: string;
  done: string;
}> = {
  mail: {
    label: 'Schuif om te versturen',
    done: 'Verstuurd'
  },
  code: {
    label: 'Schuif om goed te keuren',
    done: 'Goedgekeurd'
  },
  boekhouding: {
    label: 'Schuif om in te boeken',
    done: 'Ingeboekt'
  }
};
interface PhoneProposalProps {
  proposal: Proposal;
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
}

/** One waiting decision: a line to scan, unfolds to the content and the slide-to-confirm. */
export function PhoneProposal({
  proposal,
  onApprove,
  onReject
}: PhoneProposalProps) {
  const [open, setOpen] = useState(false);
  const lines = useDb(t => proposalLines(t, proposal), [proposal.id]);
  const a = getAgent(proposal.agent);
  const id = `pp-${proposal.id}`;
  return <li className="py-1" data-id="element-868" data-mp="1f9ozc8x6c3s5">
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} aria-controls={id} className="flex w-full items-center gap-3 rounded-2xl py-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/60" data-id="element-869" data-mp="1b8dhwqogeov1">
        <AgentMark agentId={proposal.agent} data-id="element-870" data-mp="wb4aknir9aho" />
        <span className="min-w-0 flex-1" data-id="element-871" data-mp="wceali964sqy">
          <span className="block truncate text-[16px] font-medium text-ink" data-id="element-872" data-mp="wdh1vraekx7e">{proposal.title}</span>
          <span className="block text-[13px] text-ink-muted" data-id="element-873" data-mp="2vluj0ivp0phn">
            {a.name}
            {proposal.urgency === 'nu' ? ' · nu' : proposal.urgency === 'vandaag' ? ' · vandaag' : ''}
          </span>
        </span>
        <ChevronDownIcon aria-hidden className={`h-5 w-5 shrink-0 text-ink-muted transition-transform duration-200 ${open ? 'rotate-180' : ''}`} data-id="element-874" data-mp="tqenwwwo2rm1" />
      </button>
      <AnimatePresence initial={false} data-id="element-875" data-mp="f3lfmblkem3c">
        {open && <motion.div id={id} initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.22,
        ease: EASE
      }} className="overflow-hidden" data-id="element-876" data-mp="k03foy5y9w9c">
            <div className="space-y-4 pb-4 pl-11" data-id="element-877" data-mp="yqj4emxkrmc9">
              <div className="space-y-1 text-[14px] leading-relaxed text-ink-soft" data-id="element-878" data-mp="yruc2xu55e8n">
                {lines.map(l => <p key={l} data-id="element-879" data-mp="3jg8j8zya384y">{l}</p>)}
              </div>
              <div className="flex items-center gap-2" data-id="element-880" data-mp="3ji36ikx0vcc7">
                <ProcessingTag where={proposal.where} data-id="element-881" data-mp="3nimzrdeh5vt2" />
                <span className="ml-auto" data-id="element-882" data-mp="3fy2pbs3wr3gl">
                  <WhyPopover traceId={proposal.traceId} data-id="element-883" data-mp="3fx1enoqt0ize" />
                </span>
              </div>
              <SlideToConfirm label={SLIDE[proposal.kind].label} confirmedLabel={SLIDE[proposal.kind].done} onConfirm={() => window.setTimeout(() => onApprove(proposal.id), 450)} data-id="element-884" data-mp="3axy9836wwaqw" />
              <button type="button" onClick={() => onReject(proposal.id)} className="h-10 w-full rounded-full text-[14px] font-medium text-ink-muted transition-colors duration-150 hover:bg-white/[0.06] hover:text-ink" data-id="element-885" data-mp="3kww3lmr0qyht">
                Nee, niet doen
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </li>;
}